import type { Account } from "../../types";
import type { goalTopUpLimit } from "../../lib/goal-funding";
import type { AccountAllocationSummary } from "../../lib/goals";
import { money } from "../../lib/finance";
import { formatMinor, roundToCents } from "../../lib/money-math";
import { Private } from "../../components/Privacy";

interface GoalFundingFieldsProps {
  account?: Account;
  currency: string;
  allocation?: AccountAllocationSummary;
  limit: ReturnType<typeof goalTopUpLimit>;
  amount: string;
  onAmountChange: (amount: string) => void;
}

export default function GoalFundingFields({ account, currency, allocation, limit, amount, onAmountChange }: GoalFundingFieldsProps) {
  const maxMinor = roundToCents(limit.maximumAmount);
  const selectedMinor = Number.isFinite(Number(amount)) ? roundToCents(Number(amount)) : 0;
  return (
    <div className="goal-funding-fields">
      <p className="goal-detail-sub">Earmark money already in this account for your goal. This does not transfer money or change your bank balance.</p>
      {account && allocation && (
        <div className="goal-detail-list" aria-label="Account funds">
          <div className="goal-detail-row"><span>Account balance</span><Private>{money(account.balance, currency)}</Private></div>
          <div className="goal-detail-row"><span>Already reserved for goals</span><Private>{money(allocation.goalReserved, currency)}</Private></div>
          <div className="goal-detail-row"><span>Safety buffer to keep</span><Private>{money(allocation.cushionAmount, currency)}</Private></div>
          <div className="goal-detail-row"><span>Unallocated account funds</span><Private>{money(allocation.safeToSpend, currency)}</Private></div>
          <div className="goal-detail-row"><span>Remaining to goal</span><Private>{money(limit.remainingAmount, currency)}</Private></div>
          <div className="goal-detail-row"><strong>Maximum top-up</strong><Private>{money(limit.maximumAmount, currency)}</Private></div>
        </div>
      )}
      <label>
        <span>Amount ({currency})</span>
        <input type="number" inputMode="decimal" min="0.01" max={formatMinor(maxMinor)} step="0.01"
          value={amount} onChange={e => onAmountChange(e.target.value)} required autoFocus />
      </label>
      <label>
        <span>Choose top-up amount</span>
        <input className="goal-funding-slider" type="range" min="0" max={maxMinor} step="1"
          value={Math.max(0, Math.min(maxMinor, selectedMinor))} disabled={maxMinor <= 0}
          aria-valuetext={money(Math.max(0, Math.min(maxMinor, selectedMinor)) / 100, currency)}
          onChange={e => onAmountChange(formatMinor(Number(e.target.value)))} />
      </label>
      <div className="goal-detail-row">
        <span className="goal-detail-sub">{money(0, currency)}</span>
        <button type="button" className="small" disabled={maxMinor <= 0} onClick={() => onAmountChange(formatMinor(maxMinor))}>Use maximum</button>
      </div>
      {limit.remainingAmount === 0 && <p className="goal-detail-sub" role="status">Goal target reached. No further top-up is needed.</p>}
      {maxMinor === 0 && limit.remainingAmount > 0 && <p className="form-error" role="status">No unallocated funds are available in this account. Add money to the account or remove an existing contribution first.</p>}
      {selectedMinor > maxMinor && <p className="form-error" role="alert">The amount exceeds the maximum top-up for this goal.</p>}
    </div>
  );
}
